// CAICoordinator — AI 出手排程 + 代打狀態
// 從 CGameScene 抽出：把「四家 AI 實例、AI 思考延遲排程、執行一次 AI 決策、玩家位整局代打」
// 這一整塊與狀態機 / UI 渲染無關的職責收斂在此。透過 deps 介面回呼 scene 取得 round / 共享狀態
// （lastDrawn / lastDiscarded 仍由 scene 持有，因聽牌與 UI 也讀）與代打交接時的 UI 清理。
//
// === 使用圖片 ===
// 無 — 純排程 / 決策邏輯，不渲染任何畫面

import type Phaser from 'phaser';

import { baselineDecision, decideObservation, decisionSeed } from '@ai/decision';
import { buildDecisionFrame, submitDecision, type IAIPublicContext } from '@ai/observation';
import { requestDecision, type IAIPendingDecision } from '@ai/worker-client';
import { createTableAIProfile, copyTableAIProfile, type ITableAIProfile } from '@core/table-ai';
import type { IRoundDriver } from '@core/round-driver';
import { isRemoteRound } from '@net/remote-round';
import type { TTile } from '@core/tile';
import type { TSeat } from '@core/types';

const AI_DELAY_MIN = 500; // AI 思考最短延遲 (ms)
const AI_DELAY_MAX = 1500; // AI 思考最長延遲 (ms)

export type TAICoordinatorDeps = {
  /** 用來排 delayedCall 的 scene。 */
  readonly scene: Phaser.Scene;
  /** 玩家座位（本專案固定 0，不變動）。 */
  readonly playerSeat: TSeat;
  /** 取當前牌局（每局重建，故用 getter）；本機 CRound 或伺服器牌局。 */
  readonly getRound: () => IRoundDriver;
  readonly publicContext?: IAIPublicContext;
  /** 本局是否已結束（排程觸發時防呆）。 */
  readonly isRoundEnded: () => boolean;
  /** 最近一張捨牌（scene 於 tile.discarded 記錄；reaction 決策用）。 */
  readonly getLastDiscarded: () => { tile: TTile; fromSeat: TSeat } | null;
  /** 代打交接時的 UI 清理（停 timer、關 action bar、清選取、刷新手牌）。 */
  readonly onProxyHandoff: () => void;
  /** AI 延遲思考開始／結束，用來顯示目前正在決策的座位。 */
  readonly onThinkingChange: (seat: TSeat | null) => void;
  /** 整局代打狀態改變，用來維持本家門前提示。 */
  readonly onPermanentProxyChange: (active: boolean) => void;
};

export class CAICoordinator {
  private profile = createTableAIProfile();
  private humanSeat: TSeat | null = null;
  private identity = 'preview';
  private sequence = 0;
  private permanentProxy = false;
  private paused = false;
  private destroyed = false;
  private readonly scheduled = new Map<TSeat, Phaser.Time.TimerEvent>();
  private readonly pending = new Map<TSeat, IAIPendingDecision>();
  private readonly resumeSeats = new Set<TSeat>();

  constructor(private readonly deps: TAICoordinatorDeps) {}

  configure(profile: ITableAIProfile, humanSeat: TSeat | null, identity: string): void {
    this.destroy();
    this.profile = copyTableAIProfile(profile);
    this.humanSeat = humanSeat;
    this.identity = identity;
    this.destroyed = false;
    this.paused = false;
    this.permanentProxy = false;
  }

  isPermanentProxy(): boolean {
    return this.permanentProxy;
  }
  isProxyActive(): boolean {
    return this.permanentProxy;
  }

  schedule(seat: TSeat): void {
    if (this.destroyed || this.scheduled.has(seat) || this.pending.has(seat)) return;
    if (seat === this.humanSeat && !this.permanentProxy) return;
    if (this.paused) {
      this.resumeSeats.add(seat);
      return;
    }
    const delay = AI_DELAY_MIN + Math.floor(Math.random() * (AI_DELAY_MAX - AI_DELAY_MIN));
    this.scheduled.set(
      seat,
      this.deps.scene.time.delayedCall(delay, () => {
        this.scheduled.delete(seat);
        this.startBackgroundDecision(seat);
      })
    );
    this.refreshThinkingStatus();
  }

  private startBackgroundDecision(seat: TSeat): void {
    if (this.destroyed || this.paused || this.deps.isRoundEnded()) return;
    const round = this.deps.getRound();
    if (isRemoteRound(round)) {
      this.startServerDecision(round, seat);
      return;
    }
    const frame = buildDecisionFrame(round, seat, this.deps.publicContext);
    if (!frame) {
      this.refreshThinkingStatus();
      return;
    }
    const task = requestDecision({
      taskId: `${this.identity}/${++this.sequence}/${seat}`,
      observation: frame.observation,
      profile: this.profile.seats[seat],
      proxy: seat === this.humanSeat,
    });
    this.pending.set(seat, task);
    this.refreshThinkingStatus();
    void task.result
      .then((index) => {
        if (this.pending.get(seat) !== task) return;
        this.pending.delete(seat);
        this.refreshThinkingStatus();
        if (
          index === null ||
          this.destroyed ||
          this.paused ||
          this.deps.isRoundEnded() ||
          this.deps.getRound() !== round
        )
          return;
        const current = buildDecisionFrame(round, seat, this.deps.publicContext);
        if (!current || JSON.stringify(current.observation) !== JSON.stringify(frame.observation))
          return;
        submitDecision(round, seat, current, index);
      })
      .catch((error: unknown) => console.error('[AI submit failed]', error));
  }

  /** 伺服器牌局：AI 與代打都由伺服器決定並套用，這裡只負責排程與「思考中」顯示。 */
  private startServerDecision(
    round: ReturnType<TAICoordinatorDeps['getRound']>,
    seat: TSeat
  ): void {
    if (!isRemoteRound(round)) return;
    const task: IAIPendingDecision = {
      result: round.advance(seat, seat === this.humanSeat).then(() => null),
      cancel: () => {}, // 已送出就由伺服器完成；暫停只影響之後的排程
    };
    this.pending.set(seat, task);
    this.refreshThinkingStatus();
    void task.result.finally(() => {
      if (this.pending.get(seat) !== task) return;
      this.pending.delete(seat);
      this.refreshThinkingStatus();
    });
  }

  /** 逐步預覽專用；正式對局一律使用 Worker。 */
  execute(seat: TSeat): void {
    if (seat === this.humanSeat && !this.permanentProxy) return;
    const round = this.deps.getRound();
    const frame = buildDecisionFrame(round, seat, this.deps.publicContext);
    if (!frame) return;
    const profile = this.profile.seats[seat];
    const index =
      seat === this.humanSeat
        ? baselineDecision(frame.observation, decisionSeed(frame.observation, profile.seed))
        : decideObservation(frame.observation, profile).index;
    submitDecision(round, seat, frame, index);
  }

  enablePermanentProxy(): void {
    if (this.permanentProxy) return;
    this.permanentProxy = true;
    this.deps.onPermanentProxyChange(true);
    this.deps.onProxyHandoff();
    const state = this.deps.getRound().getState();
    if (
      state.currentSeat === this.deps.playerSeat ||
      state.pendingReactionSeats.has(this.deps.playerSeat)
    )
      this.schedule(this.deps.playerSeat);
  }

  pause(): void {
    this.paused = true;
    for (const event of this.scheduled.values()) event.paused = true;
    for (const [seat, task] of this.pending) {
      this.resumeSeats.add(seat);
      task.cancel();
    }
    this.pending.clear();
    this.refreshThinkingStatus();
  }

  resume(): void {
    this.paused = false;
    for (const event of this.scheduled.values()) event.paused = false;
    for (const seat of this.resumeSeats) this.schedule(seat);
    this.resumeSeats.clear();
  }

  destroy(): void {
    this.destroyed = true;
    for (const event of this.scheduled.values()) event.remove();
    for (const task of this.pending.values()) task.cancel();
    this.scheduled.clear();
    this.pending.clear();
    this.resumeSeats.clear();
    this.deps.onThinkingChange(null);
  }

  private refreshThinkingStatus(): void {
    this.deps.onThinkingChange(
      this.pending.keys().next().value ?? this.scheduled.keys().next().value ?? null
    );
  }
}
